"use client"

import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"

import {
  useAccessModel,
  useActiveBranchSlug,
  useActiveWorkspaceSlug,
  useModulePermission,
  useShowMultiBranchWorkspaceUi,
  useUserBranchAccess,
} from "@/features/auth"
import { WorkspaceAccessModule } from "@/features/auth/model/auth-types"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { useBranchesStore } from "@/features/church/config/branches"
import { BranchStatus, BranchType, type Branch } from "@/features/church/config/branches/types/branch.types"
import {
  buildFinanceAdminUrl,
  DEFAULT_FINANCE_TAB,
  FinanceTab,
  getFinanceTabsForContext,
  isFinanceTabAllowed,
  parseFinanceTabFromPathname,
  type FinancePageContext,
} from "@/features/finance/model/finance-tabs"
import {
  resolveFinancePageContext,
  resolveWorkspaceContextBranch,
} from "@/features/finance/model/finance-branch-resolution"
import { isParishBranchAlias } from "@/shared/lib/branch/branch-admin-routing"

export function useFinancePageState() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const workspaceId = useAuthContextStore((state) => state.activeWorkspaceId)
  const workspaceSlug = useActiveWorkspaceSlug()
  const activeBranchSlug = useActiveBranchSlug()
  const showMultiBranchWorkspaceUi = useShowMultiBranchWorkspaceUi()
  const branches = useBranchesStore((state) => state.branches)
  const branchFinancePermission = useModulePermission(WorkspaceAccessModule.FINANCE)
  const accessModel = useAccessModel()
  const { accessibleBranches } = useUserBranchAccess()

  const parishBranch = React.useMemo<Branch | null>(
    () => {
      // The shared /branches store omits the SYSTEM (parish) branch, so resolve it from the auth-derived branch
      // access and only fall back to the store. Without this, parish finance scope (and the parish
      // income-category list) cannot resolve a branch id.
      //
      // Null in a MONO workspace, and correctly so: its parish is invisible and no longer listed as accessible.
      // Consumers fall back to the scope branch, which is the operational branch every mono page already uses.
      const storeParish = branches.find((branch) => branch.branchType === BranchType.System) ?? null
      if (storeParish) return storeParish

      const accessParish = accessibleBranches.find((branch) => branch.branchType === BranchType.System) ?? null
      if (!accessParish) return null

      const now = new Date().toISOString()
      return {
        id: accessParish.id,
        name: accessParish.name,
        slug: accessParish.slug,
        branchType: BranchType.System,
        timezone: accessParish.timezone,
        services: [],
        status: BranchStatus.ACTIVE,
        createdAt: now,
        updatedAt: now,
      }
    },
    [accessibleBranches, branches],
  )

  const activeBranch = React.useMemo(
    () => {
      if (isParishBranchAlias(activeBranchSlug)) {
        /*
         * The `parish` token means "this workspace's own context", and what that IS depends on the structure:
         * the parish console in a multi-branch workspace, and the single operational branch in a mono one, whose
         * parish is invisible. `accessibleBranches` no longer lists a mono parish at all, so the operational
         * branch is what remains — which is the point: one rule, one branch, same for every user in the church.
         */
        const monoBranch = showMultiBranchWorkspaceUi
          ? null
          : resolveWorkspaceContextBranch([...accessibleBranches, ...branches], {
              isMonoBranchWorkspace: true,
            })
        if (!showMultiBranchWorkspaceUi) return monoBranch as Branch | null
        return parishBranch
      }

      const fromStore = branches.find((branch) => branch.slug === activeBranchSlug)
      if (fromStore) return fromStore

      // The shared /branches store is empty for a branch-scoped role (it's gated for branch MANAGEMENT), so the
      // active branch — and thus the whole finance scope/overview — failed to resolve and the overview query was
      // disabled (empty, not forbidden). Fall back to the auth-derived branch access, which always includes the
      // user's accessible branches. Mirrors the parishBranch fallback above.
      const fromAccess = accessibleBranches.find((branch) => branch.slug === activeBranchSlug)
      if (!fromAccess) return null

      const now = new Date().toISOString()
      return {
        id: fromAccess.id,
        name: fromAccess.name,
        slug: fromAccess.slug,
        branchType: fromAccess.branchType ?? BranchType.Normal,
        services: [],
        status: BranchStatus.ACTIVE,
        createdAt: now,
        updatedAt: now,
      }
    },
    [accessibleBranches, activeBranchSlug, branches, parishBranch, showMultiBranchWorkspaceUi],
  )

  const canViewBranchFinance = branchFinancePermission.canView
  const canEditBranchFinance = branchFinancePermission.canEdit
  const canViewParishFinance = showMultiBranchWorkspaceUi && accessModel.canViewParishFinance
  const canEditParishFinance = showMultiBranchWorkspaceUi && accessModel.canEditParishFinance

  /*
   * `?branchId=` — read ONE branch's finances without leaving the branch you are in.
   *
   * The parish console needs to send a reader from a branch's overview to that branch's accounts or
   * transactions. Routing by slug would change which branch they ARE, which is a change of identity rather
   * than of page (see `canNavigateFromOverview`). A filter says "show me that one" and leaves the context
   * alone.
   *
   * Restricted to branches the reader already has access to. That is defence in depth, not the control:
   * every finance query is authorized server-side against the branch it names, so an unaccessible id comes
   * back 403 rather than data. This guard exists so the UI fails cleanly instead of rendering an error.
   */
  const branchFilterId = React.useMemo(() => new URLSearchParams(search).get("branchId")?.trim() ?? "", [search])
  const filteredBranch = React.useMemo<Branch | null>(() => {
    if (!branchFilterId) return null
    if (branchFilterId === activeBranch?.id) return null

    const fromStore = branches.find((branch) => branch.id === branchFilterId)
    if (fromStore) return fromStore

    const fromAccess = accessibleBranches.find((branch) => branch.id === branchFilterId)
    if (!fromAccess) return null

    const now = new Date().toISOString()
    return {
      id: fromAccess.id,
      name: fromAccess.name,
      slug: fromAccess.slug,
      branchType: (fromAccess.branchType as BranchType | undefined) ?? BranchType.Normal,
      timezone: fromAccess.timezone,
      services: [],
      status: BranchStatus.ACTIVE,
      createdAt: now,
      updatedAt: now,
    }
  }, [accessibleBranches, activeBranch?.id, branchFilterId, branches])

  /** What the page's data is ABOUT. The filtered branch when one is named, else the branch you are in. */
  const scopeBranch = filteredBranch ?? activeBranch

  // Context follows the scope, not the URL: filtering the parish down to one branch is branch context, or
  // the page would ask for parish-shaped data with a single branch's id.
  const activeContext: FinancePageContext = resolveFinancePageContext(scopeBranch)
  /*
   * The finance permission for THIS page, resolved once.
   *
   * No mono special case is needed any more: a mono workspace resolves its OPERATIONAL branch, so `activeContext`
   * is "branch" there and the branch grant answers. The carve-out this replaced existed only because mono used to
   * resolve the parish and then ask a parish-only question whose answer is false by construction.
   */
  const isParishContext = activeContext === "parish"
  const canViewFinance = isParishContext ? canViewParishFinance : canViewBranchFinance
  const canEditFinance = isParishContext ? canEditParishFinance : canEditBranchFinance

  const parsedTab = parseFinanceTabFromPathname(pathname, activeContext)
  const availableTabs = React.useMemo(
    () =>
      getFinanceTabsForContext(activeContext).filter(
        (tab) => showMultiBranchWorkspaceUi || tab !== FinanceTab.Transfers,
      ),
    [activeContext, showMultiBranchWorkspaceUi],
  )
  const activeTab = availableTabs.includes(parsedTab) ? parsedTab : DEFAULT_FINANCE_TAB
  React.useEffect(() => {
    if (parsedTab === activeTab) return

    navigate(
      buildFinanceAdminUrl({
        workspaceSlug,
        branchSlug: activeContext === "parish" ? "parish" : activeBranchSlug,
        tab: activeTab,
      }) + search,
      { replace: true },
    )
  }, [activeBranchSlug, activeContext, activeTab, navigate, parsedTab, search, workspaceSlug])

  const setTab = React.useCallback(
    (nextTab: FinanceTab) => {
      if (!isFinanceTabAllowed(activeContext, nextTab) || !availableTabs.includes(nextTab)) return

      navigate(
        buildFinanceAdminUrl({
          workspaceSlug,
          branchSlug: activeContext === "parish" ? "parish" : activeBranchSlug,
          tab: nextTab,
        }) + search,
        { replace: true },
      )
    },
    [activeBranchSlug, activeContext, availableTabs, navigate, search, workspaceSlug],
  )

  return {
    workspaceId,
    activeContext,
    // A SINGLE_BRANCH workspace has no parish/branch split on screen: its one operational branch IS the whole
    // church, and its parish is invisible. Kept for the few surfaces that legitimately differ in shape (a
    // transfer needs somewhere to transfer TO); it is no longer a permission signal — `canEditFinance` is.
    isMonoBranchWorkspace: !showMultiBranchWorkspaceUi,
    activeTab,
    availableTabs,
    setTab,
    activeBranch,
    parishBranch,
    scopeBranch,
    /** True when a `?branchId=` filter is narrowing the page to a branch other than the active one. */
    isBranchFiltered: filteredBranch !== null,
    /**
     * What a page should gate on: the grant that matches this page's context. Prefer these over the raw
     * branch/parish pairs below, which exist for the few screens that genuinely need to know WHICH grant they
     * hold (the global consolidated board, which is parish-only and read-only).
     */
    canViewFinance,
    canEditFinance,
    canViewBranchFinance,
    canEditBranchFinance,
    canViewParishFinance,
    canEditParishFinance,
  }
}
